import * as React from 'react';
import styled from '@emotion/styled';

export interface PopoverProps {
  /** The clickable element that opens the panel. */
  trigger: React.ReactNode;
  /** Optional bold heading inside the panel. */
  title?: React.ReactNode;
  /** Panel content. */
  children?: React.ReactNode;
  /** Horizontal edge to align the panel to. Default 'left'. */
  align?: 'left' | 'right';
  className?: string;
}

const Root = styled.div`
  position: relative;
  display: inline-block;
`;
const TriggerWrap = styled.span`display: inline-flex;`;
const Outside = styled.div`position: fixed; inset: 0; z-index: 40;`;
const Panel = styled.div<{ $align: 'left' | 'right' }>`
  position: absolute;
  top: calc(100% + 6px);
  ${({ $align }) => ($align === 'right' ? 'right: 0;' : 'left: 0;')}
  z-index: 50;
  background: ${({ theme }) => theme.color.bg.default};
  border: 1px solid ${({ theme }) => theme.color.border.default};
  border-radius: ${({ theme }) => theme.radius.base};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  padding: 12px;
  min-width: 220px;
  font-size: ${({ theme }) => theme.fontSize.sm};
`;
const Title = styled.h4`
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  font-size: ${({ theme }) => theme.fontSize.base};
  margin: 0 0 6px;
  color: ${({ theme }) => theme.color.fg.default};
`;

/** Lumen popover — click-triggered floating panel. Self-manages open/close + outside-click. */
export function Popover({ trigger, title, children, align = 'left', className }: PopoverProps) {
  const [open, setOpen] = React.useState(false);
  return (
    <Root className={className}>
      <TriggerWrap onClick={() => setOpen((o) => !o)}>{trigger}</TriggerWrap>
      {open && (
        <>
          <Outside onClick={() => setOpen(false)} />
          <Panel $align={align} role="dialog">
            {title && <Title>{title}</Title>}
            {children}
          </Panel>
        </>
      )}
    </Root>
  );
}
